import { create } from 'zustand';
import type { PgProduct } from '@/shared/types';
import { completeFirstTimeLoginInSupabase } from '@/services/supabase';
import { useProductIdStore } from '@/stores/useProductIdStore';
import { useUserDataStore } from '@/stores/useUserDataStore';

function markFirstTimeLoginComplete() {
  useUserDataStore.setState((state) => ({
    userData: { ...state.userData, firstTimeLogin: false },
  }));
  void completeFirstTimeLoginInSupabase().catch((error) => {
    console.error('Failed to mark first-time login complete:', error);
  });
}

export type OpenForTutorial =
  | 'addViz'
  | 'loadView'
  | 'dateRange'
  | 'filter'
  | 'accountMenu'
  | null;

export type TutorialId = string;

interface TutorialState {
  /** Tour is currently running (after welcome Continue or startTutorialById) */
  isRunning: boolean;
  /** Current tutorial id (e.g. 'primary', 'views-cards', 'filters'); null when not in a tour */
  currentTutorialId: TutorialId | null;
  /** Current step index within the current tutorial (0-based; -1 or not started) */
  stepIndex: number;
  /** Welcome modal is visible (only for primary tutorial, first-time) */
  welcomeOpen: boolean;
  /** Guided FilterQL Q&A wizard is open */
  guidedSetupOpen: boolean;
  /** After filter onboarding apply: expand the dashboard Filter panel once */
  expandFilterPaneOnDashboard: boolean;
  /** Spotlight tour on dashboard after guided filter apply (not a modal) */
  guidedFilterPostApplyActive: boolean;
  /** 0 = filter, 1 = record count, 2 = settings/resources */
  guidedFilterPostApplyStep: 0 | 1 | 2;
  /** Optional: which UI to open for current step (dropdowns, filter pane, etc.) */
  openForTutorial: OpenForTutorial;
  /** Node Graph Overview tutorial: product for the demo record modal (fetched when tour starts). */
  nodeGraphTutorialModalProduct: PgProduct | null;
  setNodeGraphTutorialModalProduct: (product: PgProduct | null) => void;
  setWelcomeOpen: (open: boolean) => void;
  setGuidedSetupOpen: (open: boolean) => void;
  /** Close welcome and open guided Q&A setup */
  startGuidedSetup: () => void;
  /** Close guided Q&A without marking first-run complete */
  closeGuidedSetup: () => void;
  setExpandFilterPaneOnDashboard: (expand: boolean) => void;
  /** Close build modal and start dashboard spotlight (filter → record count → settings) */
  startGuidedFilterPostApply: () => void;
  setGuidedFilterPostApplyStep: (step: 0 | 1 | 2) => void;
  endGuidedFilterPostApply: () => void;
  setStepIndex: (index: number) => void;
  setOpenForTutorial: (value: OpenForTutorial) => void;
  /** Dismiss welcome / end first-run flow and persist firstTimeLogin in Supabase */
  skip: () => void;
  /** User clicked Continue in welcome: close welcome, start primary tour */
  startTutorial: () => void;
  /** Start a specific tutorial by id (no welcome modal). Navigate to initialRoute is caller's responsibility. */
  startTutorialById: (tutorialId: TutorialId) => void;
  /** Tour finished or user closed: persist (primary only), stop tour */
  endTutorial: () => void;
  /** Replay primary from Resources: open welcome modal */
  replay: () => void;
}

export const useTutorialStore = create<TutorialState>((set, get) => ({
  isRunning: false,
  currentTutorialId: null,
  stepIndex: -1,
  welcomeOpen: false,
  guidedSetupOpen: false,
  expandFilterPaneOnDashboard: false,
  guidedFilterPostApplyActive: false,
  guidedFilterPostApplyStep: 0,
  openForTutorial: null,
  nodeGraphTutorialModalProduct: null,

  setWelcomeOpen: (open) => set({ welcomeOpen: open }),
  setGuidedSetupOpen: (open) => set({ guidedSetupOpen: open }),
  startGuidedSetup: () =>
    set({
      welcomeOpen: false,
      guidedSetupOpen: true,
      isRunning: false,
      currentTutorialId: null,
      stepIndex: -1,
    }),
  closeGuidedSetup: () => set({ guidedSetupOpen: false }),
  setExpandFilterPaneOnDashboard: (expand) => set({ expandFilterPaneOnDashboard: expand }),
  startGuidedFilterPostApply: () => {
    set({
      guidedSetupOpen: false,
      guidedFilterPostApplyActive: true,
      guidedFilterPostApplyStep: 0,
      expandFilterPaneOnDashboard: true,
    });
    void useProductIdStore.getState().fetchProductMatchCount();
  },
  setGuidedFilterPostApplyStep: (step) => set({ guidedFilterPostApplyStep: step }),
  endGuidedFilterPostApply: () => {
    set({
      guidedFilterPostApplyActive: false,
      guidedFilterPostApplyStep: 0,
      expandFilterPaneOnDashboard: false,
    });
    get().skip();
  },
  setStepIndex: (index) => set({ stepIndex: index }),
  setOpenForTutorial: (value) => set({ openForTutorial: value }),
  setNodeGraphTutorialModalProduct: (product) => set({ nodeGraphTutorialModalProduct: product }),

  skip: () => {
    markFirstTimeLoginComplete();
    set({
      welcomeOpen: false,
      guidedSetupOpen: false,
      expandFilterPaneOnDashboard: false,
      guidedFilterPostApplyActive: false,
      guidedFilterPostApplyStep: 0,
      isRunning: false,
      currentTutorialId: null,
      stepIndex: -1,
      openForTutorial: null,
      nodeGraphTutorialModalProduct: null,
    });
  },

  startTutorial: () => {
    set({
      welcomeOpen: false,
      guidedSetupOpen: false,
      expandFilterPaneOnDashboard: false,
      guidedFilterPostApplyActive: false,
      guidedFilterPostApplyStep: 0,
      isRunning: true,
      currentTutorialId: 'primary',
      stepIndex: 0,
      openForTutorial: null,
      nodeGraphTutorialModalProduct: null,
    });
  },

  startTutorialById: (tutorialId: TutorialId) => {
    if (tutorialId === 'node-graph') {
      set({
        welcomeOpen: false,
        guidedSetupOpen: false,
        guidedFilterPostApplyActive: false,
        guidedFilterPostApplyStep: 0,
        isRunning: true,
        currentTutorialId: tutorialId,
        stepIndex: 0,
        openForTutorial: null,
        nodeGraphTutorialModalProduct: null,
      });
      return;
    }
    set({
      welcomeOpen: false,
      guidedSetupOpen: false,
      guidedFilterPostApplyActive: false,
      guidedFilterPostApplyStep: 0,
      isRunning: true,
      currentTutorialId: tutorialId,
      stepIndex: 0,
      openForTutorial: null,
    });
  },

  endTutorial: () => {
    const { currentTutorialId } = get();
    const isPrimary = currentTutorialId === 'primary';
    if (isPrimary) {
      markFirstTimeLoginComplete();
    }
    set({
      isRunning: false,
      guidedSetupOpen: false,
      guidedFilterPostApplyActive: false,
      guidedFilterPostApplyStep: 0,
      currentTutorialId: null,
      stepIndex: -1,
      openForTutorial: null,
      nodeGraphTutorialModalProduct: null,
    });
  },

  replay: () => {
    set({
      welcomeOpen: true,
      guidedSetupOpen: false,
      guidedFilterPostApplyActive: false,
      guidedFilterPostApplyStep: 0,
      isRunning: false,
      currentTutorialId: null,
      stepIndex: -1,
      openForTutorial: null,
      nodeGraphTutorialModalProduct: null,
    });
  },
}));
